import React, { useCallback } from 'react';
// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
import { graphql, useFragment } from 'react-relay';
import {
	useSidebar,
	useSidebarActions,
} from '@atlassian/jira-platform-sidebar/src/controllers/use-sidebar/index.tsx';
import { SIDEBAR_EXPERIENCE_NAME as STATISTIC_EXPERIENCE } from '@atlassian/jira-issue-navigator-action-issue-field-statistics/src/common/constants.tsx';
import { SIDEBAR_EXPERIENCE_NAME as CONDITIONAL_FORMATTING_EXPERIENCE } from '@atlassian/jira-issue-navigator-action-conditional-formatting/src/common/constants.tsx';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import type { experienceRegistry_issueNavigator_SidebarExperienceRegistry_project$key } from '@atlassian/jira-relay/src/__generated__/experienceRegistry_issueNavigator_SidebarExperienceRegistry_project.graphql';
import type { experienceRegistry_issueNavigator_SidebarExperienceRegistryWithErrorBoundary_project$key } from '@atlassian/jira-relay/src/__generated__/experienceRegistry_issueNavigator_SidebarExperienceRegistryWithErrorBoundary_project.graphql';
import { fg } from '@atlassian/jira-feature-gating';
import type { experienceRegistry_issueNavigator_SidebarExperienceRegistry_view$key } from '@atlassian/jira-relay/src/__generated__/experienceRegistry_issueNavigator_SidebarExperienceRegistry_view.graphql';
import type { experienceRegistry_issueNavigator_SidebarExperienceRegistryWithErrorBoundary_view$key } from '@atlassian/jira-relay/src/__generated__/experienceRegistry_issueNavigator_SidebarExperienceRegistryWithErrorBoundary_view.graphql';
import { useResolvedJql } from '../../../services/active-jql/index.tsx';
import { IssueFieldStatisticsSidebarExperience } from './issue-field-statistics-experience/index.tsx';
import { ErrorState } from './error-state/index.tsx';
import { ConditionalFormattingSidebarExperience } from './conditional-formatting-experience/ConditionalFormattingSidebarExperience.tsx';
import { MaybeConditionalFormattingSidebarExperienceOld } from './conditional-formatting-experience/ConditionalFormattingSidebarExperienceOld.tsx';

/**
 * This component is responsible for conditionally rendering entrypoint containers for sidebar experiences supported in the issue navigator.
 * To understand how a sidebar experience is rendered, or to onboard a new experience, refer to the following steps:
 * 1. Conditionally render your entrypoint container in this file using a unique experience id.
 * 2. Call setExperienceId(experienceId) from the useSidebar hook to mount your entrypoint container.
 * 3. Load your entrypoint using entrypointActions.load() or the entrypoint button + ref pattern.
 * Depending on your experiences runtimeProps requirements, you may need to call additional hooks in this file.
 * You will likely also need to create an additional entrypoint context provider.
 */
export const SidebarExperienceRegistry = ({
	project,
	view,
}: {
	project: experienceRegistry_issueNavigator_SidebarExperienceRegistry_project$key | null;
	view: experienceRegistry_issueNavigator_SidebarExperienceRegistry_view$key | null;
}) => {
	const [{ id, content }, { closeSidebar }] = useSidebar();
	const jql = useResolvedJql();

	const projectData =
		useFragment<experienceRegistry_issueNavigator_SidebarExperienceRegistry_project$key>(
			// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
			graphql`
				fragment experienceRegistry_issueNavigator_SidebarExperienceRegistry_project on JiraProject
				@argumentDefinitions(
					isConditionalFormattingEnabled: { type: "Boolean!", defaultValue: false }
					isSavedViewConditionalFormattingEnabled: { type: "Boolean!", defaultValue: false }
				) {
					...ConditionalFormattingSidebarExperienceOld_project
						@include(if: $isConditionalFormattingEnabled)
						@skip(if: $isSavedViewConditionalFormattingEnabled)
					...ConditionalFormattingSidebarExperience_project
						@include(if: $isSavedViewConditionalFormattingEnabled)
				}
			`,
			project,
		);

	const viewData = useFragment(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment experienceRegistry_issueNavigator_SidebarExperienceRegistry_view on JiraSpreadsheetView
			@argumentDefinitions(
				isSavedViewConditionalFormattingEnabled: { type: "Boolean!", defaultValue: false }
			) {
				...ConditionalFormattingSidebarExperience_view
					@include(if: $isSavedViewConditionalFormattingEnabled)
			}
		`,
		// eslint-disable-next-line jira/ff/no-preconditioning
		fg('jira_list_conditional_formatting') && fg('saved_list_view_technical_changes') ? view : null,
	);

	if (content) {
		return content;
	}

	if (id === STATISTIC_EXPERIENCE) {
		return <IssueFieldStatisticsSidebarExperience jql={jql} onClose={closeSidebar} />;
	}

	if (id === CONDITIONAL_FORMATTING_EXPERIENCE && fg('jira_list_conditional_formatting')) {
		if (fg('saved_list_view_technical_changes')) {
			return (
				<ConditionalFormattingSidebarExperience
					project={projectData ?? null}
					view={viewData ?? null}
					onClose={closeSidebar}
				/>
			);
		}
		return (
			<MaybeConditionalFormattingSidebarExperienceOld
				project={projectData ?? null}
				onClose={closeSidebar}
			/>
		);
	}

	return null;
};

// Note: This error cannot be reset without refreshing the page, as we do not want to expose the full useSidebar hook here
// It is recommended individual experiences handle their own error boundaries, which will be automatically provided when using entrypoints
export const SidebarExperienceRegistryWithErrorBoundary = ({
	project,
	view,
}: {
	project: experienceRegistry_issueNavigator_SidebarExperienceRegistryWithErrorBoundary_project$key | null;
	view?: experienceRegistry_issueNavigator_SidebarExperienceRegistryWithErrorBoundary_view$key | null;
}) => {
	const { closeSidebar } = useSidebarActions();
	const fallback = useCallback(() => <ErrorState onClose={closeSidebar} />, [closeSidebar]);

	const projectData =
		useFragment<experienceRegistry_issueNavigator_SidebarExperienceRegistryWithErrorBoundary_project$key>(
			// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
			graphql`
				fragment experienceRegistry_issueNavigator_SidebarExperienceRegistryWithErrorBoundary_project on JiraProject
				@argumentDefinitions(
					isConditionalFormattingEnabled: { type: "Boolean!", defaultValue: false }
					isSavedViewConditionalFormattingEnabled: { type: "Boolean!", defaultValue: false }
				) {
					...experienceRegistry_issueNavigator_SidebarExperienceRegistry_project
						@arguments(
							isConditionalFormattingEnabled: $isConditionalFormattingEnabled
							isSavedViewConditionalFormattingEnabled: $isSavedViewConditionalFormattingEnabled
						)
				}
			`,
			project,
		);

	const viewData = useFragment(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment experienceRegistry_issueNavigator_SidebarExperienceRegistryWithErrorBoundary_view on JiraSpreadsheetView
			@argumentDefinitions(
				isSavedViewConditionalFormattingEnabled: { type: "Boolean!", defaultValue: false }
			) {
				...experienceRegistry_issueNavigator_SidebarExperienceRegistry_view
					@arguments(
						isSavedViewConditionalFormattingEnabled: $isSavedViewConditionalFormattingEnabled
					)
			}
		`,
		view,
	);

	return (
		<JSErrorBoundary
			id="sidebarExperienceRegistry"
			packageName="jiraIssueNavigator"
			teamName="empanada"
			fallback={fallback}
		>
			<SidebarExperienceRegistry project={projectData ?? null} view={viewData ?? null} />
		</JSErrorBoundary>
	);
};
